// Comandos do agregado Config — os Parâmetros do Sistema: rodapé da tabela de
// preços exportada, Data de Referência do Faturamento e os Tempos de Referência
// por Etapa. Dado puro; o executor só chama o domínio.

import type { DataFaturamento, Snapshot } from "../domain/snapshot.ts";
import {
  definirDataFaturamento,
  definirMesesPadraoDashboard,
  definirVolumeMinimoRanking,
  definirRodapeExport,
} from "../domain/config.ts";
import {
  definirTempoReferencia,
  type EntradaTempoReferencia,
  type UnidadeTempo,
} from "../domain/tempos-referencia.ts";
import { ETAPAS, type Etapa } from "../domain/etapas.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirTexto,
  lista,
  paraNumero,
  texto,
  umDe,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export interface EntradaTempoConfig {
  etapa: Etapa;
  /** `null` = tirar o tempo daquela etapa. */
  entrada: { pecas: number; valor: number; unidade: UnidadeTempo } | null;
}

// Os campos de união fechada são declarados com a união, não com `string`:
// alargar aqui obrigaria o executor a re-estreitar com `as`, e aí nada
// garantiria que o validador continuou usando `umDe` (ADR-0013).
export type ComandoConfig =
  | { tipo: "rodapeExport"; slogan: string; whatsapp: string }
  | { tipo: "dataFaturamento"; modo: DataFaturamento }
  | { tipo: "temposReferencia"; entradas: EntradaTempoConfig[] }
  | { tipo: "mesesPadraoDashboard"; meses: number }
  | { tipo: "volumeMinimoRanking"; pecas: number };

export function executarComandoConfig(
  snapshot: Snapshot,
  cmd: ComandoConfig,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "rodapeExport":
      return definirRodapeExport(snapshot, cmd.slogan, cmd.whatsapp);
    case "dataFaturamento":
      return definirDataFaturamento(snapshot, cmd.modo);
    case "temposReferencia": {
      let out = snapshot;
      for (const { etapa, entrada } of cmd.entradas) {
        const e: EntradaTempoReferencia | null = entrada;
        out = definirTempoReferencia(out, etapa, e);
      }
      return out;
    }
    case "mesesPadraoDashboard":
      return definirMesesPadraoDashboard(snapshot, cmd.meses);
    case "volumeMinimoRanking":
      return definirVolumeMinimoRanking(snapshot, cmd.pecas);
    default:
      return assertNunca(cmd);
  }
}

function validarEntradaTempo(valor: unknown, i: number): EntradaTempoConfig {
  const o = comoObjeto(valor, `Entrada de tempo #${i + 1}`);
  const etapa = umDe(o.etapa, `entradas[${i}].etapa`, ETAPAS);
  // Chave ausente é erro; `null` explícito é "tira o tempo dessa etapa".
  if (!("entrada" in o)) {
    throw new Error(`entradas[${i}].entrada ausente (use null para limpar).`);
  }
  if (o.entrada == null) return { etapa, entrada: null };
  const e = comoObjeto(o.entrada, `entradas[${i}].entrada`);
  return {
    etapa,
    entrada: {
      pecas: paraNumero(e.pecas),
      valor: paraNumero(e.valor),
      unidade: umDe(e.unidade, `entradas[${i}].entrada.unidade`, ["min", "h"]),
    },
  };
}

export function validarComandoConfig(valor: unknown): ComandoConfig {
  const c = comoObjeto(valor, "Comando de configuração");
  switch (c.tipo) {
    case "rodapeExport":
      return {
        tipo: "rodapeExport",
        slogan: texto(c.slogan),
        whatsapp: texto(c.whatsapp),
      };
    case "dataFaturamento":
      return {
        tipo: "dataFaturamento",
        modo: umDe(c.modo, "modo", ["coleta", "devolucao"]),
      };
    case "temposReferencia":
      return {
        tipo: "temposReferencia",
        entradas: lista(c.entradas, "entradas", validarEntradaTempo),
      };
    case "mesesPadraoDashboard":
      return { tipo: "mesesPadraoDashboard", meses: paraNumero(c.meses) };
    case "volumeMinimoRanking":
      return { tipo: "volumeMinimoRanking", pecas: paraNumero(c.pecas) };
    default:
      throw new Error(
        `Tipo de comando de configuração desconhecido: ${String(c.tipo)}.`,
      );
  }
}
